import { useEffect, useState } from "react";
import { Plus, UserCircleGear, Trash, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { PasswordStrength, evaluatePassword } from "@/components/PasswordStrength";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

export default function GranteeManagement() {
  const [rows, setRows] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [grants, setGrants] = useState([]);
  const [open, setOpen] = useState(false);

  const load = async () => setRows((await api.get("/grantees")).data);
  const loadRefs = async () => {
    setContracts((await api.get("/contracts")).data);
    setGrants((await api.get("/grants")).data);
  };
  useEffect(() => { load(); loadRefs(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Revoke this grantee's access?")) return;
    await api.delete(`/grantees/${id}`);
    toast.success("Grantee access revoked");
    load();
  };

  return (
    <div>
      <PageHeader
        overline="Grantee Access"
        title="Grantee Portal Logins"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-grantee-btn"><Plus size={16} className="mr-1.5" /> Add Grantee</Button>
            </DialogTrigger>
            <GranteeDialog contracts={contracts} grants={grants} onDone={() => { setOpen(false); load(); }} />
          </Dialog>
        }
      >
        Give external grantees / vendors a limited login. They can only see the contracts and grants you link here and
        submit reimbursements for staff review.
      </PageHeader>

      {rows.length === 0 ? (
        <EmptyState title="No grantee logins yet" subtitle="Add a grantee to give them scoped portal access." />
      ) : (
        <div className="space-y-3">
          {rows.map((g) => (
            <div key={g.id} data-testid={`grantee-row-${g.id}`} className="bg-card border border-border rounded-md p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <UserCircleGear size={18} className="text-primary" />
                  <span className="font-medium text-foreground">{g.name}</span>
                  {g.active === false && <span className="text-[10px] uppercase bg-alert/10 text-alert px-1.5 py-0.5 rounded-sm">inactive</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{g.email}</div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {g.contracts.map((c) => (
                    <span key={c.id} className="inline-flex items-center gap-1 text-[11px] bg-secondary px-2 py-0.5 rounded-sm"><LinkSimple size={11} /> {c.title || c.id}</span>
                  ))}
                  {g.grants.map((gr) => (
                    <span key={gr.id} className="inline-flex items-center gap-1 text-[11px] bg-gold/15 text-foreground px-2 py-0.5 rounded-sm"><LinkSimple size={11} /> {gr.title || gr.id}</span>
                  ))}
                  {g.contracts.length + g.grants.length === 0 && <span className="text-[11px] text-muted-foreground">No records linked</span>}
                </div>
              </div>
              <Button size="sm" variant="ghost" data-testid={`grantee-del-${g.id}`} onClick={() => remove(g.id)} className="text-alert hover:text-alert">
                <Trash size={15} className="mr-1" /> Revoke
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GranteeDialog({ contracts, grants, onDone }) {
  const [mode, setMode] = useState("invite");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [cids, setCids] = useState(new Set());
  const [gids, setGids] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (setter) => (id) => setter((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const submit = async () => {
    if (!form.name || !form.email) return toast.error("Name and email are required");
    if (mode === "direct" && !evaluatePassword(form.password).valid) return toast.error("Choose a stronger password (8+ chars, mix of upper, lower, number, symbol)");
    setBusy(true);
    try {
      const { data } = await api.post("/grantees", {
        name: form.name, email: form.email, mode,
        password: mode === "direct" ? form.password : undefined,
        contract_ids: [...cids], grant_ids: [...gids],
      });
      if (data.invited) {
        setInviteLink(data.link);
        toast.success("Invite sent — the grantee can set their own password");
      } else {
        toast.success("Grantee login created");
        onDone();
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (inviteLink) {
    return (
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-head">Invite sent</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          We emailed <b>{form.email}</b> a secure link to set their own password. You can also copy the link below and share it directly.
        </p>
        <div className="flex items-center gap-2 mt-2">
          <Input data-testid="grantee-invite-link" readOnly value={inviteLink} className="text-xs" />
          <Button data-testid="grantee-copy-link" variant="outline" onClick={() => { navigator.clipboard?.writeText(inviteLink); toast.success("Link copied"); }}>Copy</Button>
        </div>
        <DialogFooter>
          <Button data-testid="grantee-invite-done" onClick={onDone}>Done</Button>
        </DialogFooter>
      </DialogContent>
    );
  }

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle className="font-head">Add Grantee Login</DialogTitle></DialogHeader>
      <div className="flex gap-2 p-1 bg-secondary rounded-md">
        <button data-testid="grantee-mode-invite" onClick={() => setMode("invite")}
          className={`flex-1 text-sm py-1.5 rounded-sm transition-colors ${mode === "invite" ? "bg-card shadow-sm font-medium text-primary" : "text-muted-foreground"}`}>
          Email invite link
        </button>
        <button data-testid="grantee-mode-direct" onClick={() => setMode("direct")}
          className={`flex-1 text-sm py-1.5 rounded-sm transition-colors ${mode === "direct" ? "bg-card shadow-sm font-medium text-primary" : "text-muted-foreground"}`}>
          Set password now
        </button>
      </div>
      <p className="text-xs text-muted-foreground -mt-1">
        {mode === "invite"
          ? "The grantee gets a secure link to choose their own password — nothing to share manually."
          : "You set a temporary password and share it with the grantee securely."}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Full name</Label><Input data-testid="grantee-name" value={form.name} onChange={set("name")} /></div>
        <div className="space-y-1.5"><Label>Email</Label><Input data-testid="grantee-email" value={form.email} onChange={set("email")} placeholder="vendor@company.com" /></div>
        {mode === "direct" && (
          <div className="col-span-2 space-y-1.5"><Label>Temporary password</Label><Input data-testid="grantee-password" type="text" value={form.password} onChange={set("password")} placeholder="Share securely with the grantee" /><PasswordStrength value={form.password} /></div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 mt-1">
        <div>
          <Label className="overline text-muted-foreground text-[10px]">Link contracts</Label>
          <ScrollArea className="h-40 mt-1 border border-border rounded-md p-2">
            {contracts.length === 0 && <div className="text-xs text-muted-foreground p-2">No contracts</div>}
            {contracts.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                <Checkbox data-testid={`grantee-link-contract-${c.id}`} checked={cids.has(c.id)} onCheckedChange={() => toggle(setCids)(c.id)} />
                <span className="truncate">{c.title}</span>
              </label>
            ))}
          </ScrollArea>
        </div>
        <div>
          <Label className="overline text-muted-foreground text-[10px]">Link grants</Label>
          <ScrollArea className="h-40 mt-1 border border-border rounded-md p-2">
            {grants.length === 0 && <div className="text-xs text-muted-foreground p-2">No grants</div>}
            {grants.map((g) => (
              <label key={g.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
                <Checkbox data-testid={`grantee-link-grant-${g.id}`} checked={gids.has(g.id)} onCheckedChange={() => toggle(setGids)(g.id)} />
                <span className="truncate">{g.title}</span>
              </label>
            ))}
          </ScrollArea>
        </div>
      </div>
      <DialogFooter>
        <Button data-testid="grantee-submit" onClick={submit} disabled={busy}>{busy ? "Working…" : (mode === "invite" ? "Send invite" : "Create login")}</Button>
      </DialogFooter>
    </DialogContent>
  );
}
